<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher<{ win: string }>();
  
  interface Prize {
    label: string;
    color: string;
    value: string;
  }
  
  const prizes: Prize[] = [
    { label: '$10', color: '#FF6B6B', value: '$10 Cash' },
    { label: '$50', color: '#4ECDC4', value: '$50 Cash' },
    { label: '$100', color: '#45B7D1', value: '$100 Cash' },
    { label: '$250', color: '#96CEB4', value: '$250 Cash' },
    { label: '$500', color: '#FFEAA7', value: '$500 Cash' },
    { label: '$1000', color: '#DDA0DD', value: '$1000 Cash' },
    { label: 'iPhone', color: '#98D8C8', value: 'iPhone 15' },
    { label: 'PS5', color: '#F7DC6F', value: 'PlayStation 5' },
  ];
  
  let { disabled = false } = $props();
  let isSpinning = $state(false);
  let rotation = $state(0);
  let winner = $state<string | null>(null);
  
  const segmentAngle = 360 / prizes.length;
  
  function spin() {
    if (isSpinning || disabled) return;
    
    isSpinning = true;
    winner = null;
    
    const extraRotations = (5 + Math.random() * 5) * 360;
    const randomSegment = Math.floor(Math.random() * prizes.length);
    const targetRotation = rotation + extraRotations + (randomSegment * segmentAngle);
    
    rotation = targetRotation;
    
    setTimeout(() => {
      const normalizedRotation = rotation % 360;
      const winningIndex = Math.floor((360 - normalizedRotation) / segmentAngle) % prizes.length;
      winner = prizes[winningIndex].value;
      
      dispatch('win', winner);
      isSpinning = false;
    }, 4000);
  }
</script>

<div class="wheel-wrapper">
  <div class="wheel-container">
    <div class="pointer"></div>
    
    <div 
      class="wheel" 
      style="transform: rotate({rotation}deg); transition: transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99);"
    >
      {#each prizes as prize, index}
        <div 
          class="segment"
          style="background: {prize.color}; transform: rotate({index * segmentAngle}deg) skewY(-{90 - segmentAngle}deg);"
        >
          <span 
            class="segment-label"
            style="transform: skewY({90 - segmentAngle}deg) rotate({segmentAngle / 2}deg);"
          >
            {prize.label}
          </span>
        </div>
      {/each}
    </div>
    
    <div class="center-circle">
      <span class="spin-text">{isSpinning ? '...' : 'SPIN'}</span>
    </div>
    
    {#if !isSpinning && !disabled}
      <button class="spin-button" onclick={spin}>
        🎯 SPIN NOW!
      </button>
    {/if}
    
    {#if disabled}
      <div class="disabled-overlay">
        <span>🔒 Come Back Tomorrow!</span>
      </div>
    {/if}
  </div>
  
  {#if winner && !isSpinning}
    <div class="winner-announcement">
      <h3>🎉 CONGRATULATIONS! 🎉</h3>
      <p>You won: <strong>{winner}</strong></p>
    </div>
  {/if}
</div>

<style>
  .wheel-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  
  .wheel-container {
    position: relative;
    width: 350px;
    height: 350px;
    border-radius: 50%;
    box-shadow: 0 0 30px rgba(0,0,0,0.3), 0 0 60px rgba(255,215,0,0.4);
    overflow: hidden;
    border: 8px solid #FFD700;
  }
  
  .wheel {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 50%;
  }
  
  .segment {
    position: absolute;
    width: 50%;
    height: 50%;
    top: 50%;
    left: 50%;
    transform-origin: 0% 0%;
    display: flex;
    align-items: center;
    justify-content: center;
    clip-path: polygon(0 100%, 100% 0, 100% 100%);
  }
  
  .segment-label {
    position: absolute;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
    font-weight: 800;
    font-size: 1.2rem;
    color: #333;
    text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
    white-space: nowrap;
  }
  
  .pointer {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 20px solid transparent;
    border-right: 20px solid transparent;
    border-top: 40px solid #ff416c;
    z-index: 10;
    filter: drop-shadow(0 4px 6px rgba(0,0,0,0.3));
  }
  
  .center-circle {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #ff416c, #ff4b2b);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 4px solid white;
    box-shadow: 0 0 20px rgba(0,0,0,0.3);
    z-index: 5;
  }
  
  .spin-text {
    color: white;
    font-weight: 800;
    font-size: 1rem;
    letter-spacing: 1px;
  }
  
  .spin-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 15px 30px;
    font-size: 1.2rem;
    font-weight: 700;
    border-radius: 50px;
    cursor: pointer;
    z-index: 20;
    transition: all 0.3s ease;
    box-shadow: 0 5px 20px rgba(0,0,0,0.3);
  }
  
  .spin-button:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 8px 30px rgba(0,0,0,0.4);
  }
  
  .disabled-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 30;
    border-radius: 50%;
  }
  
  .disabled-overlay span {
    color: white;
    font-size: 1.3rem;
    font-weight: 700;
    text-align: center;
    padding: 20px;
  }
  
  .winner-announcement {
    margin-top: 30px;
    background: linear-gradient(135deg, #f5af19, #f12711);
    color: white;
    padding: 20px 40px;
    border-radius: 15px;
    text-align: center;
    animation: bounce 0.5s ease infinite alternate;
    box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  }
  
  @keyframes bounce {
    from { transform: translateY(0); }
    to { transform: translateY(-10px); }
  }
  
  .winner-announcement h3 {
    font-size: 1.8rem;
    margin-bottom: 10px;
  }
  
  .winner-announcement p {
    font-size: 1.5rem;
  }
  
  .winner-announcement strong {
    font-size: 2rem;
    color: #FFD700;
  }
  
  @media (max-width: 400px) {
    .wheel-container {
      width: 280px;
      height: 280px;
    }
    
    .segment-label {
      font-size: 0.9rem;
      right: 20px;
    }
  }
</style>
